/**
 * تحويل قيمة الخادم إلى رقم (يدعم سلسلة مع فاصلة عشرية).
 */
function toNum(v) {
  if (v == null || v === "") return NaN;
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  const s = String(v).trim().replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** أول رقم صالح من قائمة مرشّحات */
function firstAvg(...candidates) {
  for (const c of candidates) {
    const n = toNum(c);
    if (!Number.isNaN(n)) return n;
  }
  return NaN;
}

/** لا ندخل فى فروع ضخمة أو أسعار حتى لا نلتقط أرقاماً خاطئة */
const SKIP_DEEP_KEYS = new Set([
  "items",
  "products",
  "dishes",
  "tabs",
  "categories",
  "offers",
  "orders",
  "cart",
  "location",
  "timing",
  "pricing",
  "media",
  "delivery",
  "payments",
]);

/** مفاتيح يُتوقع أن تكون متوسط تقييم (0–5 أو أحياناً حتى 10) */
const AVG_KEY_HINTS = new Set([
  "average",
  "avg",
  "average_rating",
  "avg_rating",
  "rating_average",
  "rating_avg",
  "mean",
  "overall",
  "overall_rating",
  "score",
  "stars",
  "star_rating",
  "rate",
  "rating",
  "aggregate_rating",
  "review_average",
  "total_rating",
  "avg_score",
  "star_avg",
]);

/**
 * يبحث فى الكائن عن أول قيمة رقمية لمفتاح يشبه متوسط التقييم (حتى عمق محدود).
 */
function scanAverageFromObject(obj, depth, maxDepth) {
  if (!obj || typeof obj !== "object" || depth > maxDepth) return NaN;

  if (!Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj)) {
      if (AVG_KEY_HINTS.has(k) && v != null && typeof v !== "object") {
        const n = toNum(v);
        if (!Number.isNaN(n) && n >= 0 && n <= 10) return n;
      }
    }
  }

  for (const [k, v] of Object.entries(obj)) {
    if (SKIP_DEEP_KEYS.has(k)) continue;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const n = scanAverageFromObject(v, depth + 1, maxDepth);
      if (!Number.isNaN(n)) return n;
    }
  }

  return NaN;
}

/**
 * أرقام التقييم لعرض الهيدر — يدعم أشكال API مختلفة (متوسط + عدد المراجعات).
 */
export function getStoreRatingParts(entity) {
  if (!entity || typeof entity !== "object") {
    return { averageFormatted: "0.0", reviewsCount: 0 };
  }

  const r = entity.rating;

  let avgNum = NaN;

  if (r != null && typeof r === "object" && !Array.isArray(r)) {
    avgNum = firstAvg(
      r.average,
      r.average_rating,
      r.avg_rating,
      r.avg,
      r.score,
      r.value,
      r.mean,
      r.rate,
      r.rating,
      r.star_rating,
      r.stars,
      r.overall,
      r.overall_rating,
      r.total_rating,
      r.aggregate_rating,
      r.review_average,
      r.avg_score,
      r.star_avg
    );

    if (Number.isNaN(avgNum)) {
      avgNum = scanAverageFromObject(r, 0, 3);
    }
  } else if (typeof r === "number" || typeof r === "string") {
    avgNum = toNum(r);
  }

  if (Number.isNaN(avgNum)) {
    avgNum = firstAvg(
      entity.average_rating,
      entity.avg_rating,
      entity.rating_average,
      entity.rating_avg,
      entity.star_rating,
      entity.stars,
      entity.rate,
      entity.overall_rating,
      entity.aggregate_rating,
      entity.reviews?.average,
      entity.reviews?.average_rating,
      entity.reviews?.avg_rating,
      entity.reviews?.avg,
      entity.review_stats?.average,
      entity.review_statistics?.average,
      entity.ratings_summary?.average,
      entity.stats?.average_rating,
      entity.stats?.rating,
      entity.summary?.rating,
      entity.summary?.average_rating,
      entity.meta?.average_rating,
      entity.meta?.rating?.average,
      entity.meta?.avg_rating,
      entity.ratings?.average,
      entity.ratings?.avg,
      entity.ratings?.average_rating,
      entity.review_rating,
      entity.store_rating
    );
  }

  if (Number.isNaN(avgNum)) {
    avgNum = scanAverageFromObject(entity, 0, 2);
  }

  const averageFormatted = !Number.isNaN(avgNum) ? avgNum.toFixed(1) : "0.0";

  let countRaw =
    typeof r === "object" && r
      ? firstAvg(
          r.reviews_count,
          r.total_count,
          r.count,
          r.total_reviews,
          r.reviews_total,
          r.comments_count
        )
      : NaN;

  if (Number.isNaN(countRaw)) {
    countRaw = firstAvg(
      entity.reviews_count,
      entity.total_reviews,
      entity.reviews_total,
      entity.rating_count,
      entity.comments_count,
      entity.reviews?.total,
      entity.reviews?.count,
      entity.stats?.reviews_count,
      r?.reviews_count
    );
  }

  const reviewsCountSafe =
    Number.isFinite(countRaw) && !Number.isNaN(countRaw)
      ? Math.round(countRaw)
      : 0;

  return {
    averageFormatted,
    reviewsCount: reviewsCountSafe,
  };
}
